import React, { useState, useEffect } from 'react';
import { TourTypePriceTier } from '../../../types/tourType.types';
import './PriceTierEditor.css';

interface PriceTierEditorProps {
  priceTiers: TourTypePriceTier[];
  onChange: (tiers: TourTypePriceTier[]) => void;
  basePricePerPerson: number;
}

export const PriceTierEditor: React.FC<PriceTierEditorProps> = ({
  priceTiers,
  onChange,
  basePricePerPerson
}) => {
  const [errors, setErrors] = useState<string[]>([]);

  const addTier = () => {
    const maxExisting = priceTiers.length > 0
      ? Math.max(...priceTiers.map(t => t.maxParticipants || 9999))
      : 0;

    const newTier: TourTypePriceTier = {
      id: 0, // Will be set by backend
      tourTypeId: 0,
      minParticipants: maxExisting + 1,
      maxParticipants: maxExisting + 10,
      pricePerPerson: basePricePerPerson,
      sortOrder: priceTiers.length,
      isActive: true
    };

    onChange([...priceTiers, newTier]);
  };

  const updateTier = (index: number, field: keyof TourTypePriceTier, value: any) => {
    const updated = [...priceTiers];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeTier = (index: number) => {
    const updated = priceTiers.filter((_, i) => i !== index);
    onChange(updated);
  };

  useEffect(() => {
    validateTiers(priceTiers);
  }, [priceTiers]);

  const validateTiers = (tiers: TourTypePriceTier[]) => {
    const newErrors: string[] = [];

    // Check for overlaps
    for (let i = 0; i < tiers.length; i++) {
      for (let j = i + 1; j < tiers.length; j++) {
        const tierA = tiers[i];
        const tierB = tiers[j];

        const aMin = tierA.minParticipants;
        const aMax = tierA.maxParticipants || 9999;
        const bMin = tierB.minParticipants;
        const bMax = tierB.maxParticipants || 9999;

        if ((aMin <= bMax && aMax >= bMin)) {
          newErrors.push(`Staffel ${i + 1} und ${j + 1} überlappen sich`);
        }
      }
    }

    // Check for gaps
    const sorted = [...tiers].sort((a, b) => a.minParticipants - b.minParticipants);
    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];

      if (current.maxParticipants && next.minParticipants > current.maxParticipants + 1) {
        newErrors.push(
          `Lücke zwischen Staffel ${i + 1} und ${i + 2}: ` +
          `${current.maxParticipants + 1}-${next.minParticipants - 1} Personen nicht abgedeckt`
        );
      }
    }

    setErrors(newErrors);
  };

  return (
    <div className="price-tier-editor">
      <div className="price-tier-header">
        <h3>Preisstaffeln</h3>
        <button
          type="button"
          onClick={addTier}
          className="btn-add-tier"
        >
          + Staffel hinzufügen
        </button>
      </div>

      {errors.length > 0 && (
        <div className="price-tier-errors">
          {errors.map((error, index) => (
            <p key={index} className="error-message">{error}</p>
          ))}
        </div>
      )}

      {priceTiers.length === 0 ? (
        <div className="price-tier-empty">
          <p>Keine Staffelpreise definiert.</p>
          <p className="text-muted">
            Es wird der Basispreis verwendet: {basePricePerPerson.toFixed(2)} €
          </p>
        </div>
      ) : (
        <div className="price-tier-list">
          {priceTiers.map((tier, index) => (
            <div key={index} className="price-tier-item">
              <div className="price-tier-fields">
                <div className="form-group">
                  <label>Von (Personen)</label>
                  <input
                    type="number"
                    min="1"
                    value={tier.minParticipants}
                    onChange={(e) => updateTier(index, 'minParticipants', parseInt(e.target.value))}
                  />
                </div>

                <div className="form-group">
                  <label>Bis (Personen)</label>
                  <input
                    type="number"
                    min={tier.minParticipants}
                    value={tier.maxParticipants || ''}
                    onChange={(e) => updateTier(
                      index,
                      'maxParticipants',
                      e.target.value ? parseInt(e.target.value) : null
                    )}
                    placeholder="∞"
                  />
                </div>

                <div className="form-group">
                  <label>Preis pro Person (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={tier.pricePerPerson}
                    onChange={(e) => updateTier(index, 'pricePerPerson', parseFloat(e.target.value))}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => removeTier(index)}
                className="btn-remove-tier"
                title="Staffel löschen"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {priceTiers.length > 0 && (
        <div className="price-tier-info">
          <p>
            <strong>Hinweis:</strong> Staffelpreise werden automatisch nach Personenanzahl sortiert.
            Wenn keine Staffeln definiert sind, wird der Basispreis ({basePricePerPerson.toFixed(2)} €) verwendet.
          </p>
        </div>
      )}
    </div>
  );
};
